import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Form, Badge, Table } from "react-bootstrap";
import { 
    Search, X, Funnel
} from "react-bootstrap-icons";
import { createClient } from "@supabase/supabase-js";
import "../styles//inventory.css"; // Import your CSS file for styling

// Supabase configuration
const supabaseUrl = "https://aeauzrauwtlwvhzafpwm.supabase.co";
const supabaseKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlYXV6cmF1d3Rsd3ZoemFmcHdtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzkyMjM0OTgsImV4cCI6MjA1NDc5OTQ5OH0.mWw7stDMNG-ceiUqA50bcrkcNxf0HhUtSfzh3nd-t7Y";
const supabase = createClient(supabaseUrl, supabaseKey);
const BACKDOOR_PASSWORD = "DFSJJD5646-&*&^JKDF"; // change this to your own password


const Inventory = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [stockFilter, setStockFilter] = useState("in_stock");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
  const checkAuth = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const password = urlParams.get("password");

    if (password === BACKDOOR_PASSWORD) {
      console.warn("Access granted via backdoor password");
      setIsAuthenticated(true);
      fetchProducts();
      return;
    }

    // Check normal Supabase session
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) throw error;

      setIsAuthenticated(true);
      fetchProducts();
    } catch (err) {
      console.error("Authentication check failed:", err);
      navigate('/admin');
    }
  };

  checkAuth();

  const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_OUT') {
      navigate('/admin');
    }
  });

  return () => {
    authListener?.subscription?.unsubscribe();
  };
}, [navigate]);


  const fetchProducts = async () => {
  try {
    setLoading(true);
    let allProducts = [];
    let from = 0;
    const batchSize = 1000; // Supabase max per query
    
    while (true) {
      const { data, error, count } = await supabase
        .from("products_inventory")
        .select("*", { count: 'exact' })
        .range(from, from + batchSize - 1);
      
      if (error) throw error;
      
      allProducts = [...allProducts, ...data];
      from += batchSize;
      
      // Stop if we've fetched all records
      if (!data.length || data.length < batchSize) break;
    }

    const inventoryData = allProducts.map(product => ({
      ...product,
      stock_quantity: product.stock_quantity || 0,
      reference: product.reference || `REF-${product.id.toString().padStart(4, '0')}`,
      purchase_price_ttc: product.purchase_price_ttc || 0,
      brand: product.brand || "Non spécifié"
    }));
    
    setProducts(inventoryData);
    setError(null);
    console.log(`Fetched ${inventoryData.length} products`);
  } catch (err) {
    setError("Impossible de charger l'inventaire. Veuillez réessayer plus tard.");
    console.error("Fetch error:", err);
  } finally {
    setLoading(false);
  }
};


  // Get unique categories and brands for filter dropdowns
  const categories = useMemo(() => {
    const categorySet = new Set(products.map(product => product.category));
    return Array.from(categorySet);
  }, [products]);
  const brands = useMemo(() => {
    const brandSet = new Set(products.map(product => product.brand != null ? product.brand : "Non spécifié"));
    return Array.from(brandSet).filter(brand => brand);
  }, [products]);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesCategory = selectedCategory === "all" || product.category === selectedCategory;
      const matchesBrand = selectedBrand === "all" || (selectedBrand === "Non spécifié" && !product.brand) || product.brand === selectedBrand;
      const matchesSearch = searchQuery === "" || 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        product.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.reference?.toLowerCase().includes(searchQuery.toLowerCase());
      let matchesStock = true;
      if (stockFilter === "in_stock") matchesStock = product.stock_quantity > 0;
      // else if (stockFilter === "low_stock") matchesStock = product.stock_quantity > 0 && product.stock_quantity <= 5;
      else if (stockFilter === "out_of_stock") matchesStock = product.stock_quantity === 0;
      // else if (stockFilter === "medium_stock") matchesStock = product.stock_quantity > 5 && product.stock_quantity <= 20;
      // else if (stockFilter === "high_stock") matchesStock = product.stock_quantity > 20;
      const matchesPriceRange = 
        (priceRange.min === "" || product.price >= parseInt(priceRange.min)) &&
        (priceRange.max === "" || product.price <= parseInt(priceRange.max));
      
      return matchesCategory && matchesBrand && matchesSearch && matchesStock && matchesPriceRange;
    });
  }, [selectedCategory, selectedBrand, stockFilter, searchQuery, products, priceRange]);

const importInventoryFromCSV = async (csvFile) => {
  try {
    setLoading(true);
    
    // 1. Delete all existing records
    const { error: deleteError } = await supabase
      .from('products_inventory')
      .delete()
      .neq('id', 0); // Delete all records (neater than .delete() without conditions)

    if (deleteError) throw deleteError;

    // 2. Parse the CSV file
    const text = await csvFile.text();
    const lines = text.split('\n');
    const headers = lines[0].split(',').map(h => h.trim());
    
    const productsToInsert = lines.slice(1).map(line => {
      if (!line.trim()) return null;
      
      const values = line.split(',');
      const product = {};
      
      headers.forEach((header, index) => {
        // Handle cases where there might be commas in the values
        let value = values[index] ? values[index].trim() : '';
        
        // Convert numeric fields
        if (['price', 'stock_quantity', 'purchase_price_ttc'].includes(header)) {
          value = parseFloat(value) || 0;
        }
        
        product[header] = value;
      });
      
      return product;
    }).filter(Boolean); // Remove any null entries

    // 3. Insert new records in batches (Supabase has a limit per request)
    const batchSize = 100;
    for (let i = 0; i < productsToInsert.length; i += batchSize) {
      const batch = productsToInsert.slice(i, i + batchSize);
      
      const { error: insertError } = await supabase
        .from('products_inventory')
        .insert(batch);
        
      if (insertError) throw insertError;
    }

    // Refresh the inventory
    await fetchProducts();
    alert('Inventaire mis à jour avec succès!');
    
  } catch (error) {
    console.error('Error importing inventory:', error);
    alert(`Erreur lors de l'import: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="inventory-section py-5">
      <Container fluid className="py-4">
        {/* Search and Controls */}
        <div className="search-container">
          <div className="search-input-wrapper ">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Rechercher par nom, marque ou référence..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button   
                className="clear-search-btn"
                onClick={() => setSearchQuery("")}
                aria-label="Effacer la recherche"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="filter-controls">
            <button 
              className="filter-toggle"
              onClick={() => setShowFilters(!showFilters)}
              aria-expanded={showFilters}
            >
              <Funnel className="filter-icon" />
              <span>Filtres avancés</span>
            </button>
          </div>
        </div>
        {/* Advanced Filters */}
        <div className={`filters-container ${showFilters ? 'show' : ''}`}>
          <Row className="g-3">
            <Col xs={6} sm={4}>
              <Form.Group>
                <Form.Label className="filter-label">Catégorie</Form.Label>
                <Form.Select 
                  value={selectedCategory} 
                  onChange={(e) => setSelectedCategory(e.target.value)} 
                  className="filter-select"
                >
                  <option value="all">Toutes</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={6} sm={4}>
              <Form.Group>
                <Form.Label className="filter-label">Marque</Form.Label>
                <Form.Select 
                  value={selectedBrand} 
                  onChange={(e) => setSelectedBrand(e.target.value)} 
                  className="filter-select"
                >
                  <option value="all">Toutes</option>
                  {brands.map((brand) => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} sm={4}>
              <Form.Group>
                <Form.Label className="filter-label">Stock</Form.Label>
                <Form.Select 
                  value={stockFilter} 
                  onChange={(e) => setStockFilter(e.target.value)} 
                  className="filter-select"
                >
                  <option value="all">Tous</option>
                  <option value="in_stock">En stock</option>
                  <option value="out_of_stock">Rupture</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          {/* <Row className="g-3 mt-2">
            <Col xs={6} sm={4}>
              <Form.Group>
                <Form.Label className="filter-label">Prix min</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Min"
                  value={priceRange.min}
                  onChange={(e) => setPriceRange({...priceRange, min: e.target.value})}
                  className="filter-select"
                />
              </Form.Group>
            </Col>
            <Col xs={6} sm={4}>
              <Form.Group>
                <Form.Label className="filter-label">Prix max</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Max"
                  value={priceRange.max}
                  onChange={(e) => setPriceRange({...priceRange, max: e.target.value})}
                  className="filter-select"
                />
              </Form.Group>
            </Col>
          </Row> */}
        </div>
        {/* Loading State */}
        {loading && (
          <div className="loading-container">
            <div className="loader"></div>
            <p>Chargement de l'inventaire...</p>
          </div>
        )}
        {/* Error State */}
        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}
        {/* Table View Only */}
        {!loading && !error && (
          <>
            <div className="table-container">
              <Table responsive striped hover className="inventory-table">
                <thead>
                  <tr>
                    <th className="col-1">Référence</th>
                    <th className="col-6">Nom</th>
                    <th className="col-1">Catégorie</th>
                    <th className="col-1">Marque</th>
                    <th className="col-1">Stock</th>
                    <th className="col-1">Prix achat TTC</th>
                    <th className="col-1">Prix vente</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => (
                    <tr key={product.id}>
                      <td><code>{product.reference}</code></td>
                      <td className="product-name-cell">{product.name}</td>
                      <td>
                        <Badge bg="secondary" size="sm">{product.category}</Badge>
                      </td>
                      <td>{product.brand || "Non spécifié"}</td>
                      <td>{product.stock_quantity}</td>
                      <td className="price-cell">{product.purchase_price_ttc?.toLocaleString("fr-FR")} DA</td>
                      <td className="price-cell">{product.price?.toLocaleString("fr-FR")} DA</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {filteredProducts.length === 0 && (
              <div className="no-results">
                <h3>Aucun produit trouvé</h3>
                <p>Essayez de modifier vos critères de recherche</p>
              </div>
            )}
          </>
        )}
      </Container>
    <div className="import-controls">
  <label htmlFor="csv-upload" className="import-btn">
    Importer CSV
  </label>
  <input
    id="csv-upload"
    type="file"
    accept=".csv"
    onChange={(e) => {
      if (e.target.files.length > 0) {
        if (confirm('Êtes-vous sûr de vouloir remplacer tout l\'inventaire? Cette action est irréversible.')) {
          importInventoryFromCSV(e.target.files[0]);
        }
      }
    }}
    style={{ display: 'none' }}
  />
</div>
    </div>

  );
};

export default Inventory;